import { stopwordList } from "../../types/StopwordList";
import { IXSifterQueryRule, IXSifterQuery, IXSifterQueryRoot } from "../../types/XSifterQueries"
import { v4 as uuidv4 } from 'uuid';

const translateQueryRule = (rule: any): IXSifterQueryRule => {
    return {
        field: rule.field,
        operator: rule.operator,
        value: String(rule.value) || null,
        combinator: rule.combinator ? String(rule.combinator).toUpperCase() : undefined,
        isNot: rule.isNot,
    }
}

const untranslateQueryRule = (rule: IXSifterQueryRule): any => {
    return {
        id: uuidv4(),
        field: rule.field,
        operator: rule.operator,
        valueSource: "value",
        value: rule.value ?? undefined
    }
}

const untranslateQueryRules = (queryRules: (IXSifterQueryRule | IXSifterQuery)[] | null | undefined): any => {
    if (!queryRules || queryRules.length === 0)
        return undefined;

    let ret: any[] = []
    for (let i=0; i < queryRules.length; i++) {
        if (!queryRules[i].hasOwnProperty('field')) {
            // it's a combination with more rules
            ret.push({
                id: uuidv4(),
                combinator: (queryRules[i].combinator === 'OR' ? 'or' : 'and'),
                not: queryRules[i].isNot,
                rules: untranslateQueryRules(queryRules[i].rules)
            })
        } else {
            ret.push(untranslateQueryRule(queryRules[i] as IXSifterQueryRule));
        }
    }
    return ret;
}

const translateQueryRules = (queryRules: any, includeRetweets: boolean): Array<IXSifterQueryRule> | null => {
    if (!queryRules || queryRules.length === 0) {
        return null;
    }

    let retRules: any[] = [];
    for (var i = 0; i < queryRules.length; i++) {
        if (!queryRules[i].hasOwnProperty('field')) {
            retRules.push(translateQuery(queryRules[i], includeRetweets, false));
        } else {
            retRules.push(translateQueryRule(queryRules[i]));
        }
    }

    return retRules;
}

export const untranslateQuery = (query: IXSifterQuery): any => {
    return {
        id: uuidv4(),
        combinator: (query.combinator === 'OR' ? 'or' : 'and'),
        not: query.isNot,
        rules: untranslateQueryRules(query.rules)
    };
}

const translateQuery = (queryJson: any, includeRetweets: boolean, isRoot: boolean, fromDate?: string | null, toDate?: string | null): IXSifterQuery => {
    if (isRoot) {
        if (includeRetweets) {
            let ret: IXSifterQueryRoot = {
                combinator: queryJson.combinator.toUpperCase(),
                isNot: queryJson.isNot,
                rules: (queryJson.rules && queryJson.rules.length > 0) ? translateQueryRules(queryJson.rules, includeRetweets) : null,
                fromDate: fromDate,
                toDate: toDate,
            }
            return ret;
        }

        let innerRules: IXSifterQuery = {
            // this group's own combinator now only signals how it joins the PRECEDING sibling
            // (excludeRetweets) in the outer rules array - it must always be "AND" so the
            // retweet exclusion is ANDed in, regardless of the query's original top-level
            // combinator (which is preserved per-rule on queryJson.rules, not here).
            combinator: "AND",
            isNot: queryJson.isNot,
            rules: (queryJson.rules && queryJson.rules.length > 0) ? translateQueryRules(queryJson.rules, includeRetweets) : null,
        };

        let excludeRetweets: IXSifterQueryRule = {
            combinator: "AND",
            field: "is",
            isNot: true,
            operator: "retweet",
            value: "false"
        };

        let rules = [excludeRetweets, innerRules];

        let returnValue: IXSifterQueryRoot = {
                combinator: "AND",
                isNot: false,
                rules: rules,
                fromDate: fromDate,
                toDate: toDate,
            }
        return returnValue;
    }

    let queryReturn: IXSifterQuery = {
        combinator: queryJson.combinator.toUpperCase(),
        isNot: queryJson.isNot,
        rules: (queryJson.rules && queryJson.rules.length > 0) ? translateQueryRules(queryJson.rules, includeRetweets) : null,
    };

    return queryReturn;
}

export const translateQueryToXApi = (query: any, includeRetweets: boolean, fromDate?: string | null, toDate?: string | null): IXSifterQueryRoot | null => {
    if (!query || !query.rules || query.rules.length === 0) {
        return null;
    }

    let queryJson = JSON.parse(JSON.stringify(query));
    return translateQuery(queryJson, includeRetweets, true, fromDate, toDate);
}

export const checkStripNoRetweets = (query: IXSifterQuery): IXSifterQuery => {
    if (query.combinator !== 'AND' || query.isNot === true)
        return query;
    if (query.rules?.length !== 2)
        return query;
    if (query.rules[0].combinator === 'AND' && query.rules[0].isNot === true) {
        let asInitialQueryRule = query.rules[0] as IXSifterQueryRule;
        if (asInitialQueryRule && asInitialQueryRule.field === 'is' && asInitialQueryRule.operator === 'retweet') {
            return query.rules[1];
        }
    }
    if (query.rules[0].combinator !== 'AND' || query.rules[0].isNot === false)
        return query;
    if (query.rules[0].rules?.length !== 1)
        return query;
    let asQueryRule = query.rules[0].rules[0] as IXSifterQueryRule;

    if (!asQueryRule || asQueryRule.field !== 'is' || asQueryRule.operator !== 'retweet')
        return query;

    return query.rules[1];
}

const checkForEmptyQueryGroups = (query: IXSifterQuery | null): boolean => {
    if (query === null)
        return true;

    if (query.hasOwnProperty("rules"))
    {
        if (query.rules === undefined || query.rules === null || query.rules?.length === 0)
            return false;

        for (let i = 0; i < (query.rules?.length ?? 0); i++) {
            if (!checkForEmptyQueryGroups(query.rules[i]))
                return false;
        }
    }

    return true;
}

const checkForIsHasLangBySelf = (query: IXSifterQuery | null, parent: IXSifterQuery | null): boolean => {
    if (query === null)
        return true;

    if (query.hasOwnProperty("rules") && query.rules !== undefined && query.rules !== null && query.rules?.length > 0)
    {
        if (query.rules.length === 1 && query.rules[0].hasOwnProperty("field")) {
            let asRuleValue = (query.rules[0] as IXSifterQueryRule);
            if (asRuleValue.field === 'has' || asRuleValue.field === 'is' || asRuleValue.field === 'lang') {
                if (parent !== null && parent.combinator === 'OR')
                return false;            
            }
        }

        for (let i = 0; i < (query.rules?.length ?? 0); i++) {
            if (!checkForIsHasLangBySelf(query.rules[i], query))
                return false;
        }
    }

    return true;
}

const checkAtLeastOnePositiveNonStopword  = (query: IXSifterQuery | null, parent: IXSifterQuery | null): boolean => {
    if (query === null)
        return true;

    if (query.hasOwnProperty("rules") && query.rules !== undefined && query.rules !== null && query.rules?.length > 0)
    {
        for (let i = 0; i < (query.rules?.length ?? 0); i++) {
            if (query.rules[i].hasOwnProperty("value")) {
                let asRuleValue = (query.rules[i] as IXSifterQueryRule);
                if (!stopwordList.includes(asRuleValue.value ?? "")) {
                    return true;
                }
            } else {
                if (checkAtLeastOnePositiveNonStopword(query.rules[i], query)) {
                    return true;
                }
            }
        }
    }

    return false;
}

export const isValidXQuery = (query: IXSifterQueryRoot | null): string | null => {
    if (!query)
        return null;

    let stripped = checkStripNoRetweets(query);

    // no empty groups
    if (!checkForEmptyQueryGroups(stripped)) {
        return "You cannot have empty groups in your query.";
    }

     // is / has / lang cannot be in a group by themselves
     if (!checkForIsHasLangBySelf(stripped, null)) {
        return "An 'is', 'has', or 'language' clause cannot be in a group by itself with an OR clause.";
     }
   
    // rules must contain at least one positive non-stopword clause
    if (!checkAtLeastOnePositiveNonStopword(stripped, null))
    {
        return "Rules must contain at least one positive non-stopword clause.";
    }

    return null;
}
